import React, { useRef, useEffect, useCallback } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { WebView } from 'react-native-webview';

/** Reusable syntax-highlighted HTML/Handlebars code editor — NOT quotation-
 * specific. A real editor (tag/attribute/string/{{placeholder}} highlighting,
 * Tab-to-indent, line numbers) rather than a plain multiline text input,
 * built the same WebView+postMessage way as CanvasBuilder.tsx so both
 * components share one architecture for editing the same `htmlContent`
 * string — this editor's "canvas" is just source text instead of a live DOM. */

export interface HtmlCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const buildEditorHTML = (placeholder: string) => `
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; height: 100%; background: #1e1e2e; }
    #wrap { position: relative; height: 100%; display: flex; }
    #gutter {
      flex: 0 0 auto; padding: 10px 8px 10px 10px; text-align: right; user-select: none;
      color: #6b7280; font: 12px/1.6 'Menlo', 'Consolas', monospace; background: #181825;
      white-space: pre;
    }
    #editorArea { position: relative; flex: 1; height: 100%; overflow: hidden; }
    #highlight, #editor {
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      margin: 0; padding: 10px; border: none; outline: none; resize: none;
      font: 12px/1.6 'Menlo', 'Consolas', monospace;
      white-space: pre-wrap; word-break: break-word; overflow: auto;
    }
    #highlight { color: #e4e4e7; pointer-events: none; background: transparent; }
    #editor { background: transparent; color: transparent; caret-color: #f4f4f5; z-index: 1; }
    #editor::placeholder { color: #6b7280; }
    .tag { color: #7dd3fc; }
    .attr { color: #fca5a5; }
    .str { color: #86efac; }
    .hb { color: #fde047; }
    .cmt { color: #6b7280; font-style: italic; }
  </style>
</head>
<body>
  <div id="wrap">
    <div id="gutter"></div>
    <div id="editorArea">
      <pre id="highlight"></pre>
      <textarea id="editor" spellcheck="false" autocapitalize="off" autocorrect="off" placeholder="${placeholder.replace(/"/g, '&quot;')}"></textarea>
    </div>
  </div>
  <script>
    const editor = document.getElementById('editor');
    const hl = document.getElementById('highlight');
    const gutter = document.getElementById('gutter');
    let suppressEmit = false;

    function send(obj) {
      const msg = JSON.stringify(obj);
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(msg);
      else if (window.parent) window.parent.postMessage(msg, '*');
    }

    function escapeHtml(s) {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function highlight(src) {
      let out = escapeHtml(src);
      out = out.replace(/(&lt;!--[\\s\\S]*?--&gt;)/g, '<span class="cmt">$1</span>');
      out = out.replace(/(\\{\\{[^}]*\\}\\})/g, '<span class="hb">$1</span>');
      out = out.replace(/(&lt;\\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:\\s+[a-zA-Z_:-]+(?:=("|')[^"']*\\4)?)*)(\\s*\\/?&gt;)/g,
        function (m, open, tag, attrs, q, close) {
          const attrHtml = attrs.replace(/([a-zA-Z_:-]+)(=)("|')([^"']*)(\\3)/g,
            '<span class="attr">$1</span>$2<span class="str">$3$4$5</span>');
          return open + '<span class="tag">' + tag + '</span>' + attrHtml + close;
        });
      return out + '\\n';
    }

    function updateGutter() {
      const lines = editor.value.split('\\n').length;
      let out = '';
      for (let i = 1; i <= lines; i++) out += i + '\\n';
      gutter.textContent = out;
    }

    function render() {
      hl.innerHTML = highlight(editor.value);
      updateGutter();
      syncScroll();
    }

    function syncScroll() {
      hl.scrollTop = editor.scrollTop;
      hl.scrollLeft = editor.scrollLeft;
      gutter.scrollTop = editor.scrollTop;
    }

    editor.addEventListener('input', () => {
      render();
      if (!suppressEmit) send({ type: 'changed', value: editor.value });
    });
    editor.addEventListener('scroll', syncScroll);
    editor.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = editor.selectionStart, end = editor.selectionEnd;
        editor.value = editor.value.slice(0, start) + '  ' + editor.value.slice(end);
        editor.selectionStart = editor.selectionEnd = start + 2;
        render();
        send({ type: 'changed', value: editor.value });
      }
    });

    function handleMessage(raw) {
      let data;
      try { data = JSON.parse(raw); } catch (e) { return; }
      if (data.type === 'load') {
        suppressEmit = true;
        editor.value = data.value || '';
        render();
        suppressEmit = false;
      } else if (data.type === 'insertAtCursor') {
        const start = editor.selectionStart, end = editor.selectionEnd;
        editor.value = editor.value.slice(0, start) + data.text + editor.value.slice(end);
        const pos = start + data.text.length;
        editor.selectionStart = editor.selectionEnd = pos;
        render();
        send({ type: 'changed', value: editor.value });
      }
    }

    window.addEventListener('message', (e) => handleMessage(e.data));
    if (window.ReactNativeWebView) document.addEventListener('message', (e) => handleMessage(e.data));

    render();
    send({ type: 'editorReady' });
  </script>
</body>
</html>
`;

export const HtmlCodeEditor: React.FC<HtmlCodeEditorProps> = ({ value, onChange, placeholder = '' }) => {
  const webViewRef = useRef<WebView>(null);
  const readyRef = useRef(false);
  const lastEmittedRef = useRef<string | null>(null);
  const editorHTML = buildEditorHTML(placeholder);

  const postToEditor = useCallback((obj: object) => {
    const msg = JSON.stringify(obj);
    if (Platform.OS === 'web') {
      const iframe = document.getElementById('html-code-editor-iframe') as HTMLIFrameElement | null;
      if (iframe?.contentWindow) iframe.contentWindow.postMessage(msg, '*');
    } else {
      webViewRef.current?.postMessage(msg);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const handler = (event: MessageEvent) => {
      const iframe = document.getElementById('html-code-editor-iframe') as HTMLIFrameElement | null;
      if (iframe && event.source === iframe.contentWindow) handleEditorMessage({ data: event.data });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (!readyRef.current) return;
    if (value === lastEmittedRef.current) return;
    postToEditor({ type: 'load', value });
  }, [value, postToEditor]);

  const handleEditorMessage = (event: any) => {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (data.type === 'editorReady') {
        readyRef.current = true;
        postToEditor({ type: 'load', value });
      } else if (data.type === 'changed') {
        lastEmittedRef.current = data.value;
        onChange(data.value);
      }
    } catch (_) {}
  };

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? (
        <iframe id="html-code-editor-iframe" srcDoc={editorHTML} style={{ width: '100%', height: '100%', border: 'none' } as any} />
      ) : (
        <WebView
          ref={webViewRef}
          source={{ html: editorHTML }}
          style={{ flex: 1, backgroundColor: '#1e1e2e' }}
          onMessage={handleEditorMessage}
          javaScriptEnabled
          domStorageEnabled
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 320, borderRadius: 8, overflow: 'hidden' },
});
